import { createBrowserRouter, Navigate } from "react-router-dom";

// Layouts
import { PublicLayout } from "./layouts/PublicLayout";
import { AppLayout } from "./layouts/AppLayout";
import { GameLayout } from "./layouts/GameLayout";
import { AdminLayout } from "./layouts/AdminLayout";

// Guards
import { AuthGuard } from "./guards/AuthGuard";
import { GuestGuard } from "./guards/GuestGuard";
import { AdminGuard } from "./guards/AdminGuard";

// Public pages
import { LandingPage } from "./pages/LandingPage";
import { LoginPage } from "./pages/LoginPage";
import { RegisterPage } from "./pages/RegisterPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { PricingPage } from "./pages/PricingPage";
import { AboutPage } from "./pages/AboutPage";

// Blog pages
import {
  BlogListPage,
  BlogPostPage,
  BlogCategoryPage,
  BlogErrorPage,
} from "./pages/blog";

// Legal pages (accessible to all users)
import { PrivacyPolicyPage } from "./pages/PrivacyPolicyPage";
import { TermsOfServicePage } from "./pages/TermsOfServicePage";

// Public utility pages
import { EmailPreferencesPage } from "./pages/EmailPreferencesPage";

// Protected pages
import { ProfilesPage } from "./pages/ProfilesPage";
import { PlayPage } from "./pages/PlayPage";
import { DisciplinePage } from "./pages/DisciplinePage";
import { LevelPage } from "./pages/LevelPage";
import { ChallengePage } from "./pages/ChallengePage";
import { ParentDashboardPage } from "./pages/ParentDashboardPage";
import { ProgressReportPage } from "./pages/ProgressReportPage";
import { ReferralPage } from "./pages/ReferralPage";
import { SettingsPage } from "./pages/SettingsPage";

// Error page
import { NotFoundPage } from "./pages/NotFoundPage";

// Admin pages
import {
  AdminDashboardPage,
  UserListPage,
  UserDetailPage,
  UserChildrenPage,
  ChildDetailPage,
  ChildProgressPage,
  AnalyticsPage,
  ProgressAnalytics,
  ConversionAnalytics,
  EmailDashboardPage,
  EmailSequencesPage,
  UserEmailHistoryPage,
  AdminSettingsPage,
  ChallengesPage,
  ChallengeDetailPage,
  SkillsPage,
  AIHistoryPage,
} from "./pages/admin";

/**
 * JumpBean Route Configuration
 *
 * Route structure:
 * - `/` - Landing page (unauthenticated)
 * - `/login` - Parent login
 * - `/register` - Parent registration
 * - `/forgot-password` - Password reset request
 * - `/pricing` - Subscription pricing (public, no auth redirect)
 * - `/about` - About JumpBean page
 * - `/blog` - Blog listing (public, no auth redirect)
 * - `/blog/:category` - Blog category listing
 * - `/blog/:category/:slug` - Individual blog post
 * - `/privacy` - Privacy policy (public)
 * - `/terms` - Terms of service (public)
 * - `/profiles` - Child profile selection (protected)
 * - `/play` - Main game hub (protected)
 * - `/play/:discipline` - Discipline level map (protected)
 * - `/play/:discipline/:level` - Level challenges (protected)
 * - `/challenge/:id` - Active challenge (protected, game layout)
 * - `/parent` - Parent dashboard (protected)
 * - `/parent/referrals` - Referral program dashboard (protected)
 * - `/settings` - Settings page (protected)
 *
 * Admin routes (admin role required):
 * - `/admin` - Admin dashboard
 * - `/admin/users` - User list
 * - `/admin/users/:profileId` - User detail
 * - `/admin/users/:profileId/children` - User's children
 * - `/admin/children/:childId` - Child detail
 * - `/admin/analytics` - Analytics dashboard
 * - `/admin/analytics/progress` - Progress analytics
 * - `/admin/analytics/conversions` - Conversion analytics
 * - `/admin/emails` - Email dashboard
 * - `/admin/emails/users/:profileId` - User email history
 * - `/admin/challenges` - Challenge management with skill filtering
 * - `/admin/skills` - Skills taxonomy overview
 * - `/admin/ai-history` - AI generation history
 * - `/admin/settings` - Admin settings
 */
export const router = createBrowserRouter([
  // Public routes - redirect to /profiles if already logged in
  {
    element: <GuestGuard />,
    children: [
      {
        element: <PublicLayout />,
        children: [
          {
            path: "/",
            element: <LandingPage />,
          },
          {
            path: "/login",
            element: <LoginPage />,
          },
          {
            path: "/register",
            element: <RegisterPage />,
          },
          {
            path: "/forgot-password",
            element: <ForgotPasswordPage />,
          },
          {
            path: "/about",
            element: <AboutPage />,
          },
        ],
      },
    ],
  },

  // Blog routes - public, accessible to all users (no auth redirect)
  {
    element: <PublicLayout />,
    errorElement: <BlogErrorPage />,
    children: [
      {
        path: "/blog",
        element: <BlogListPage />,
      },
      {
        path: "/blog/:category",
        element: <BlogCategoryPage />,
      },
      {
        path: "/blog/:category/:slug",
        element: <BlogPostPage />,
      },
    ],
  },

  // Pricing - accessible to all users (both guests and authenticated)
  {
    element: <PublicLayout />,
    children: [
      {
        path: "/pricing",
        element: <PricingPage />,
      },
    ],
  },

  // Protected routes - redirect to /login if not logged in
  {
    element: <AuthGuard />,
    children: [
      // App layout routes (with bottom navigation)
      {
        element: <AppLayout />,
        children: [
          {
            path: "/profiles",
            element: <ProfilesPage />,
          },
          {
            path: "/play",
            element: <PlayPage />,
          },
          {
            path: "/play/:discipline",
            element: <DisciplinePage />,
          },
          {
            path: "/play/:discipline/:level",
            element: <LevelPage />,
          },
          {
            path: "/parent",
            element: <ParentDashboardPage />,
          },
          {
            path: "/parent/reports/:childId",
            element: <ProgressReportPage />,
          },
          {
            path: "/parent/referrals",
            element: <ReferralPage />,
          },
          {
            path: "/settings",
            element: <SettingsPage />,
          },
        ],
      },

      // Game layout routes (minimal chrome for gameplay)
      {
        element: <GameLayout />,
        children: [
          {
            path: "/challenge/:id",
            element: <ChallengePage />,
          },
        ],
      },
    ],
  },

  // Legal pages - accessible to all users (both guests and authenticated)
  {
    element: <PublicLayout />,
    children: [
      {
        path: "/privacy",
        element: <PrivacyPolicyPage />,
      },
      {
        path: "/terms",
        element: <TermsOfServicePage />,
      },
      {
        path: "/email-preferences",
        element: <EmailPreferencesPage />,
      },
    ],
  },

  // Admin routes - require admin role
  {
    element: <AdminGuard />,
    children: [
      {
        element: <AdminLayout />,
        children: [
          {
            path: "/admin",
            element: <AdminDashboardPage />,
          },
          {
            path: "/admin/users",
            element: <UserListPage />,
          },
          {
            path: "/admin/users/:profileId",
            element: <UserDetailPage />,
          },
          {
            path: "/admin/users/:profileId/children",
            element: <UserChildrenPage />,
          },
          {
            path: "/admin/children/:childId",
            element: <ChildDetailPage />,
          },
          {
            path: "/admin/children/:childId/progress",
            element: <ChildProgressPage />,
          },
          {
            path: "/admin/analytics",
            element: <AnalyticsPage />,
            children: [
              {
                path: "progress",
                element: <ProgressAnalytics />,
              },
              {
                path: "conversions",
                element: <ConversionAnalytics />,
              },
            ],
          },
          {
            path: "/admin/emails",
            element: <EmailDashboardPage />,
          },
          {
            path: "/admin/emails/sequences",
            element: <EmailSequencesPage />,
          },
          {
            path: "/admin/users/:profileId/emails",
            element: <UserEmailHistoryPage />,
          },
          {
            path: "/admin/challenges",
            element: <ChallengesPage />,
          },
          {
            path: "/admin/challenges/:id",
            element: <ChallengeDetailPage />,
          },
          {
            path: "/admin/skills",
            element: <SkillsPage />,
          },
          {
            path: "/admin/ai-history",
            element: <AIHistoryPage />,
          },
          {
            path: "/admin/settings",
            element: <AdminSettingsPage />,
          },
        ],
      },
    ],
  },

  // 404 - Not Found
  {
    path: "*",
    element: <NotFoundPage />,
  },
]);
